import React from 'react';
import { ProductShell, MobileVideoMock, GuestExperienceRow } from './ProductShell';
import { LeadIn } from './shared';
import RentAiBenefits from './RentAiBenefits';
import ResolveVisual from './ResolveVisual';
import AcademyCollage from './AcademyCollage';

// Phone-frame walkthrough videos. When the clips are ready, set these to
// their URLs (portrait ~9:19 MP4, muted-autoplay friendly) — e.g. a Vercel
// Blob URL or `/img/turo-alternative/videos/rentdirect-demo.mp4`. Leaving
// them null shows the "Video coming soon" placeholder.
const RENTDIRECT_PHONE_VIDEO =
  'https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/rentdirect-demo-hq.mp4';
const RENTID_PHONE_VIDEO =
  'https://ti6mkfot02n3tn3d.public.blob.vercel-storage.com/media/rentid-demo-hq.mp4';

/* ================================================================== */
/* RentDirect™                                                         */
/* ================================================================== */

export const RentDirectBlock = ({ logo, bg, bgSubtle }) => (
  <ProductShell
    name="RentDirect™"
    logo={logo}
    bg={bg}
    bgSubtle={bgSubtle}
    tagline="Your branded booking engine and guest experience."
    description={
      <>
        Customized white-labeled booking platform connects your fleet, customers, calendars,
        agreements, payments, and workflows in one place. Guests can manage and modify trips,
        message your team, and upload photos throughout their rental.{' '}
        <span className="mpa-desc-strong">All included. All connected. All in your control.</span>
      </>
    }
    descriptionStyle={{ color: '#f4f2f4' }}
    headerRight={<GuestExperienceRow />}
    aside={<MobileVideoMock videoSrc={RENTDIRECT_PHONE_VIDEO} />}
    apiLink={{ label: 'Explore the direct rental API', href: '/transition-plan' }}
    subhead="Automated rental lifecycle management"
    features={[
      'White-labeled rental website',
      'Booking & fleet management',
      'Dedicated guest accounts',
      'Custom rental agreements',
      'Credit & cash payments',
      'Security deposits',
      'Pre- and post-trip photos',
      'Automated workflows',
      'Analytics & reporting',
      'Roadside assistance',
      'Toll collection & reconciliation',
    ]}
  />
);

/* ================================================================== */
/* RentID™                                                             */
/* ================================================================== */

export const RentIdBlock = ({ logo, bg }) => (
  <>
    <LeadIn
      eyebrow="Trust & safety tools & workflows"
      title="Going direct means managing risk, you decide who gets the keys."
      sub="It's more than looking at a driver's license. When you operate independently, trust and safety becomes part of your business."
      subLarge
    />
    <ProductShell
      name="RentID™"
      logo={logo}
      bg={bg}
      tagline="De-risk direct rentals with driver verification services."
      description="Driver verification, risk tools, and automated workflows built directly into your rental process helping you make more informed decisions before handing over the keys."
      descriptionStyle={{ color: '#ffffff' }}
      aside={<MobileVideoMock videoSrc={RENTID_PHONE_VIDEO} fit="contain" />}
      subhead="Access the same tools marketplaces use, but you're in control"
      features={[
        'ID & selfie fraud checks',
        'Active/Suspended DL validation',
        'Criminal record checks',
        'Driver insurance validation',
        'DMV motor vehicle reports',
        'Driver block list database',
        'Verification dashboard',
        'Verification certificate of record',
      ]}
      closingLine="More information. Better rental decisions."
      apiLink={{ label: 'Explore the verification API', href: '/transition-plan' }}
    />
  </>
);

/* ================================================================== */
/* RentAI™                                                             */
/* ================================================================== */

const AGENTS = [
  {
    name: 'Less Booking Admin',
    copy: 'Automate reservation updates, trip changes, availability checks, renter communication, and routine booking workflows.',
  },
  {
    name: 'Faster Renter Screening',
    copy: 'Coordinate verification steps, surface risk signals, and bring important renter information to your attention before vehicle release.',
  },
  {
    name: 'Simpler Trip Logistics',
    copy: 'Keep pickups, deliveries, returns, vehicle movements, and trip logistics organized without constant manual coordination.',
  },
  {
    name: 'Smarter Daily Operations',
    copy: 'Surface active rentals, exceptions, overdue tasks, and fleet activity so you can focus on what needs attention most.',
  },
  {
    name: 'Less Back-Office Work',
    copy: "Automate repetitive documentation, follow-ups, data entry, and administrative tasks that consume your team's time.",
  },
  {
    name: 'Faster Issue Resolution',
    copy: 'Organize incidents, violations, evidence, claims documentation, and post-trip workflows so problems can be handled more efficiently.',
  },
  {
    name: 'Scale Without Overhead',
    copy: 'Automate repetitive rental work so you can manage more vehicles and trips without adding administrative overhead.',
  },
  {
    name: 'Prioritize What Matters',
    copy: 'Surface important tasks, exceptions, and decisions so you know what needs your attention.',
  },
];

export const RentAiBlock = ({ logo, bg }) => (
  <>
    <LeadIn
      eyebrow="burden-free direct rentals"
      title="More rentals, less headaches"
      sub="Running your own rental business gives you more control. That shouldn't mean spending your entire day managing repetitive back-office tasks."
      subLarge
    />
    <ProductShell
      name="RentAI™"
      logo={logo}
      bg={bg}
      tagline="AI native platform with context aware rental agents to manage daily tasks."
      description="AI-native rental management platform with context-aware agents built to manage operational, logistical, and administrative rental tasks. You stay in control of every decision, with approval before action is taken."
      extra={<RentAiBenefits items={AGENTS} logo={logo} label="Get more done faster with less headaches" />}
    />
  </>
);

/* ================================================================== */
/* RentSuccess™                                                        */
/* ================================================================== */

export const RentSuccessBlock = ({ logo, bg }) => (
  <>
    <LeadIn
      eyebrow="Post rental collections"
      title="What happens when there's an incident?"
      sub="Tolls. Tickets. Late returns. Rental violations. Damage. Claims. Direct rental operators now have the infrastructure for these exact rental moments."
      subLarge
    />
    <ProductShell
      name="RentSuccess™"
      logo={logo}
      bg={bg}
      tagline="Your rental back office totally automated for what happens next."
      description="Document, manage, track, and take action before, during, and after every trip with automated support and administrative workflows."
      subhead="Resolution infrastructure for your rental business."
      aside={<ResolveVisual />}
      asideWide
      features={[
        'Resolution Center',
        'Trip violations',
        'Customizable fees',
        'Insurance claims',
        'Toll reconciliation',
        'Ticket documentation',
        'Refund management',
        'Review management',
        'Evidence gathering',
        'Telematics tracking',
      ]}
      closingLine="Your business. Your policies. Your decisions."
    />
  </>
);

/* ================================================================== */
/* RentAcademy™                                                        */
/* ================================================================== */

export const RentAcademyBlock = ({ logo, bg }) => (
  <>
    <LeadIn
      eyebrow="You've learned how to host"
      title="Learn how to operate from the pros"
      sub="Marketplace hosting and running an independent rental business aren't exactly the same thing. Get the knowledge to thrive."
      subLarge
    />
    <ProductShell
      name="RentAcademy™"
      logo={logo}
      bg={bg}
      tagline="Build your independent operator playbook from industry experts."
      description="RentAcademy™ gives operators education and resources designed around running a modern rental business. Learn how the pieces fit together as you move from marketplace hosting toward independent operations."
      aside={<AcademyCollage />}
      subhead="Learn as you transition."
      features={[
        'Training & education center',
        'Platform education',
        'Rental business education',
        'Compliance resources',
        'Safety guidance',
        'Insurance education',
        'Claims education',
        'Operator playbooks',
        'Best practices',
      ]}
      closingLine="Don't just go direct. Learn how to operate direct."
    />
  </>
);
